import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@evocha/api-client";
import { formatMoney, type Minor } from "@evocha/shared-types";
import { adminApi } from "./api";

export interface Paged<T> {
  data: T[];
  total?: number;
  meta?: { page: number; page_size: number; total?: number };
}

export const tzs = (minor: Minor | number | null | undefined) =>
  formatMoney(minor ?? 0, "TZS");

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.problem.detail ?? error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong. Try again.";
}

export function shortDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

export function shortDate(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export function useDebouncedValue<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function useAdminQuery<T>(
  key: readonly unknown[],
  path: string,
  query: Record<string, string | number | boolean | undefined> = {},
  enabled = true,
  pollIntervalMs?: number,
) {
  return useQuery({
    queryKey: ["admin", ...key, query],
    queryFn: () => adminApi.get<T>(path, { query }),
    enabled,
    ...(pollIntervalMs ? { refetchInterval: pollIntervalMs, refetchIntervalInBackground: false } : {}),
  });
}

export function downloadCsv(filename: string, header: string[], rows: Array<Array<string | number>>) {
  const csv = [
    header.join(","),
    ...rows.map((row) =>
      row
        .map((cell) => (typeof cell === "string" && /[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell))
        .join(","),
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
